import React, { forwardRef, ReactNode } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';
import { genericMemo } from '@direct-frontend/stdlib';

import { TestProps } from '../../internal/types/testTypes';
import { DataAttributesProps } from '../../internal/types/dataAttributesTypes';
import { cn } from '../../lib/css/className/className';
import { useObjectRef } from '../../hooks/useObjectRef';

import './Card.css';

const cls = cn('Card');

export type CardColor =
    | 'white'
    | 'white-2'
    | 'gray'
    | 'gray-2'
    | 'gray-3'
    | 'gray-4'
    | 'gray-5'
    | 'red'
    | 'red-2'
    | 'red-3'
    | 'green'
    | 'green-2'
    | 'green-3'
    | 'blue'
    | 'blue-2'
    | 'blue-3'
    | 'purple'
    | 'purple-2'
    | 'purple-3'
    | 'orange'
    | 'yellow'
    | 'yellow-2'
    | 'yellow-3'
    | 'yellow-4'
    | 'black'
    | 'transparent'
    | 'strict-black'
    | 'strict-gray'
    | 'strict-white'
    | 'strict-white-2'
    | 'ai-high-contrast';

export type BorderColor = 'gray' | 'red';
export type CardPadding = '8' | '12' | '14' | '16' | '20' | '24' | '28' | '36' | '48' | '64';
export type CardBorderRadius = '4' | '8' | '10' | '12' | '14' | '20' | '24';
export type CardPaddingCorrection = 'top' | 'top-bottom';

export interface CardProps extends TestProps, DataAttributesProps {
    /**
     * Дочерние элементы
     */
    children: ReactNode;

    /**
     * Цвет фона элемента
     * @default transparent
     */
    color?: CardColor | 'current';

    /**
     * Цвет обводки элемента
     * @default none
     */
    borderColor?: BorderColor;

    /**
     * Отступ со всех сторон элемента (рекомендуется устанавливать тот, что слева/справа в макете).
     * @default none
     */
    padding?: CardPadding;

    /**
     * Корректировка отступов сверху и снизу.
     * Используется, когда требуется сделать верхний либо верхний и нижний отступ меньше, чем по бокам.
     */
    paddingCorrection?: CardPaddingCorrection;

    /**
     * Скругление углов элемента
     * @default none
     */
    borderRadius?: CardBorderRadius;

    /**
     * Корректировка скругления верхних углов
     * @default false
     */
    topBorderRadiusCorrection?: boolean;

    /**
     * Корректировка скругления нижних углов
     * @default false
     */
    bottomBorderRadiusCorrection?: boolean;

    /**
     * Названия классов для элемента.
     * Для минимизации нод при выравнивании можно миксовать с классами из хуков `useVStack` и `useHStack`.
     */
    className?: string;

    /**
     * Обработчик нажатия на элемент
     */
    onClick?: () => void;

    /**
     * Атрибут id для элемента
     */
    id?: string;

    /**
     * Признак отключения взаимодействия с Card
     */
    isDisabled?: boolean;
}

/**
 * Компонент для создания контейнеров с заданными отступами, цветом фона, обводки и скруглением углов.
 */
export const Card = genericMemo(
    forwardRef<HTMLDivElement, CardProps>(function Card(props, forwardedRef) {
        const {
            className,
            children,
            color = 'transparent',
            borderColor,
            padding,
            paddingCorrection,
            borderRadius,
            topBorderRadiusCorrection = false,
            bottomBorderRadiusCorrection = false,
            id,
            isDisabled,
            onClick,
            'data-testid': dataTestId,
            ...otherProps
        } = props;

        const ref = useObjectRef(forwardedRef);
        const { buttonProps } = useButton({ onPress: onClick, elementType: 'div', isDisabled }, ref);
        const { focusProps, isFocusVisible } = useFocusRing();
        const isInteractive = Boolean(onClick) && !isDisabled;

        return (
            <div
                {...(onClick && mergeProps(buttonProps, focusProps))}
                {...otherProps}
                className={cls(
                    {
                        color,
                        borderColor,
                        padding,
                        paddingCorrection,
                        borderRadius,
                        topBorderRadiusCorrection,
                        bottomBorderRadiusCorrection,
                        focused: isFocusVisible,
                        interactive: isInteractive,
                    },
                    [className],
                )}
                data-testid={dataTestId}
                id={id}
                ref={ref}
            >
                {children}
            </div>
        );
    }),
);
